import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import InventoryQuantity from "./inventory_quantity";

const focusableSelector = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
].join(",");

const AddRemoveStockModal = ({ inventory, closeModal }) => {
  const dialogRef = useRef(null);
  const [formState, setFormState] = useState({
    addedQuantity: 0,
    removedQuantity: 0,
    updatedQuantity: inventory.quantity,
    reason: "",
    initials: ""
  });

  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusableElements = Array.from(dialogRef.current.querySelectorAll(focusableSelector));
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    dialogRef.current?.querySelector("input")?.focus();
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prevState) => {
      const isQuantityChange = name === "addedQuantity" || name === "removedQuantity";
      const newValue = isQuantityChange ? parseInt(value, 10) : value;

      const newQuantities = {
        addedQuantity: name === "addedQuantity" ? newValue : prevState.addedQuantity,
        removedQuantity: name === "removedQuantity" ? newValue : prevState.removedQuantity
      };

      const newUpdatedQuantity =
        (inventory.quantity || 0) +
        (newQuantities.addedQuantity || 0) -
        (newQuantities.removedQuantity || 0);

      setIsError(
        (newQuantities.removedQuantity || 0) >
          (inventory.quantity || 0) + (newQuantities.addedQuantity || 0)
      );

      return {
        ...prevState,
        [name]: newValue,
        updatedQuantity: newUpdatedQuantity
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isError) return;

    const formData = new FormData();
    formData.append("inventory[updated_quantity]", formState.updatedQuantity);
    formData.append("inventory[added_quantity]", formState.addedQuantity);
    formData.append("inventory[removed_quantity]", formState.removedQuantity);
    formData.append("inventory[reason]", formState.reason);
    formData.append("inventory[initial]", formState.initials);

    fetch(`/inventories/${inventory.id}/update_inventory_stock`, {
      method: "PATCH",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector("[name=csrf-token]").content
      }
    }).then((response) => {
      if (response.ok) {
        window.location.reload();
      } else {
        response.json().then((data) => {
          alert(data.error);
        });
      }
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="modal is-active inventory-stock-modal">
        <button
          type="button"
          className="modal-background"
          aria-label="Close stock update"
          tabIndex={-1}
          onClick={closeModal}
        ></button>
        <div
          ref={dialogRef}
          className="modal-card ccw-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="inventory-stock-dialog-title"
        >
          <header className="modal-card-head ccw-dialog__header">
            <div className="modal-card-title inventory-stock-modal__title-row">
              <h1 id="inventory-stock-dialog-title">{inventory.name}</h1>
              <InventoryQuantity quantity={inventory.quantity} />
            </div>
            <button
              type="button"
              className="inventory-stock-modal__close"
              aria-label="Close stock update"
              onClick={closeModal}
            >
              <span aria-hidden="true">×</span>
            </button>
          </header>
          <section className="modal-card-body ccw-dialog__body">
            <div className="modal-subtitle inventory-stock-modal__summary">
              <h2>{inventory.category || "No category"}</h2>
              {inventory.description && <p>{inventory.description}</p>}
            </div>

            <div className="inventory-stock-modal__quantity-grid">
              <div className="ccw-field">
                <label className="label ccw-field__label" htmlFor="inventory-added-quantity">
                  Add stock
                </label>
                <input
                  id="inventory-added-quantity"
                  className="input ccw-input"
                  type="number"
                  name="addedQuantity"
                  value={formState.addedQuantity}
                  onChange={handleInputChange}
                  min="0"
                  onWheel={(e) => e.target.blur()}
                />
              </div>
              <div className="ccw-field">
                <label className="label ccw-field__label" htmlFor="inventory-removed-quantity">
                  Remove stock
                </label>
                <input
                  id="inventory-removed-quantity"
                  className="input ccw-input"
                  type="number"
                  name="removedQuantity"
                  value={formState.removedQuantity}
                  onChange={handleInputChange}
                  min="0"
                  onWheel={(e) => e.target.blur()}
                />
              </div>
            </div>

            {isError && (
              <div className="field error ccw-notice ccw-notice--danger" role="alert">
                Remove stock cannot be greater than the available quantity.
              </div>
            )}

            <div className="inventory-stock-modal__new-quantity">
              <span className="ccw-field__label">New quantity</span>
              <InventoryQuantity quantity={formState.updatedQuantity} />
            </div>

            <div className="ccw-field">
              <label className="label ccw-field__label" htmlFor="inventory-stock-initials">
                Your initials
              </label>
              <input
                id="inventory-stock-initials"
                className="input ccw-input"
                type="text"
                name="initials"
                value={formState.initials}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="ccw-field">
              <label className="label ccw-field__label" htmlFor="inventory-stock-reason">
                Reason
              </label>
              <textarea
                id="inventory-stock-reason"
                className="textarea ccw-textarea"
                name="reason"
                value={formState.reason}
                onChange={handleInputChange}
                required
              ></textarea>
            </div>
          </section>
          <footer className="modal-card-foot inventory-stock-modal__actions">
            <button
              type="submit"
              className="button is-primary ccw-button ccw-button--primary"
              disabled={isError}
            >
              Update stock
            </button>
            <button
              type="button"
              className="button ccw-button ccw-button--secondary"
              onClick={closeModal}
            >
              Cancel
            </button>
          </footer>
        </div>
      </div>
    </form>
  );
};

AddRemoveStockModal.propTypes = {
  inventory: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    category: PropTypes.string,
    quantity: PropTypes.number.isRequired,
    description: PropTypes.string,
    updated_at: PropTypes.string.isRequired
  }),
  closeModal: PropTypes.func.isRequired
};
export default AddRemoveStockModal;
